/**
 * As contas da tela de Bater Ponto — as MESMAS do painel admin.
 *
 * Copiadas de `apps/admin/src/pages/BaterPonto.tsx` (project-green): o relógio
 * do escritório mostra o que a tela do painel mostra, e a única maneira de os
 * dois números nunca discordarem é fazerem a mesma conta. Aqui ficam puras — o
 * `agora` entra por argumento — para se poderem testar.
 *
 * `data_hora` é a hora LOCAL de quem bateu, sem fuso (`2026-09-15T07:30:09`),
 * tal como o painel a guarda; `new Date()` lê-a no fuso do navegador, que é o
 * que o painel também faz.
 */
import type { PontoRegistro } from "@greenvoice/shared";

const pad = (n: number) => String(n).padStart(2, "0");

/** A data LOCAL em `YYYY-MM-DD` — nunca `toISOString`, que é a de UTC. */
export function dataLocal(d: Date = new Date()): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** A segunda-feira da semana de `d`, em `YYYY-MM-DD` local. */
export function segundaLocal(d: Date): string {
  const dia = d.getDay();
  const segunda = new Date(d);
  segunda.setDate(d.getDate() - (dia === 0 ? 6 : dia - 1));
  return dataLocal(segunda);
}

const quando = (r: PontoRegistro) => new Date(r.data_hora).getTime();

/** Soma os troços fechados e diz se o último ficou aberto. */
function somar(regs: readonly PontoRegistro[]): { ms: number; entrada: Date | null; aTrabalhar: boolean } {
  let ms = 0;
  let entrada: Date | null = null;
  let aTrabalhar = false;
  // No mesmo segundo, a ordem é a de gravação (id) — é a mesma regra da API.
  for (const r of [...regs].sort((a, b) => quando(a) - quando(b) || a.id - b.id)) {
    const t = new Date(r.data_hora);
    if (r.tipo === "Entrada") {
      entrada = t;
      aTrabalhar = true;
    } else if (r.tipo === "Início almoço" && entrada) {
      ms += t.getTime() - entrada.getTime();
      entrada = null;
      aTrabalhar = false;
    } else if (r.tipo === "Retorno almoço") {
      entrada = t;
      aTrabalhar = true;
    } else if (r.tipo === "Saída" && entrada) {
      ms += t.getTime() - entrada.getTime();
      entrada = null;
      aTrabalhar = false;
    }
  }
  return { ms, entrada, aTrabalhar };
}

/**
 * `calcHours` do painel: `Xh Ym`. Um troço aberto só conta se for de HOJE — uma
 * saída esquecida noutro dia não soma horas até ao fim dos tempos.
 */
export function horas(regs: readonly PontoRegistro[], agora: Date): string {
  if (!regs.length) return "";
  const soma = somar(regs);
  let ms = soma.ms;
  if (soma.entrada && soma.aTrabalhar && dataLocal(soma.entrada) === dataLocal(agora)) {
    ms += agora.getTime() - soma.entrada.getTime();
  }
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
}

/** `calcularTempoTrabalhado` do painel: `Xh Ym Zs` de um dia, e se ainda está a trabalhar. */
export function tempoDoDia(regs: readonly PontoRegistro[], agora: Date): { texto: string; aTrabalhar: boolean } {
  if (!regs.length) return { texto: "", aTrabalhar: false };
  const soma = somar(regs);
  let ms = soma.ms;
  if (soma.entrada && soma.aTrabalhar) ms += agora.getTime() - soma.entrada.getTime();
  if (ms <= 0) return { texto: "", aTrabalhar: soma.aTrabalhar };
  const s = Math.floor(ms / 1000);
  return {
    texto: `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m ${s % 60}s`,
    aTrabalhar: soma.aTrabalhar,
  };
}

/** As batidas de um dia (`YYYY-MM-DD`). */
export function doDia(regs: readonly PontoRegistro[], data: string): PontoRegistro[] {
  return regs.filter((r) => r.data_hora.startsWith(data));
}

/** As batidas da semana (segunda a domingo) do dia escolhido. */
export function daSemana(regs: readonly PontoRegistro[], data: string): PontoRegistro[] {
  const escolhido = new Date(`${data}T00:00:00`);
  const dia = escolhido.getDay();
  const inicio = new Date(escolhido);
  inicio.setDate(escolhido.getDate() - (dia === 0 ? 6 : dia - 1));
  inicio.setHours(0, 0, 0, 0);
  const fim = new Date(inicio);
  fim.setDate(inicio.getDate() + 6);
  fim.setHours(23, 59, 59, 999);
  return regs.filter((r) => {
    const d = new Date(r.data_hora);
    return d >= inicio && d <= fim;
  });
}

export interface ResumoSemana {
  inicio: string;
  fim: string;
  horas: string;
  semanaActual: boolean;
}

/** O resumo mensal do painel: uma entrada por semana que tenha batidas nesse mês. */
export function resumoDoMes(regs: readonly PontoRegistro[], mes: string, agora: Date): ResumoSemana[] {
  const [ano, numero] = mes.split("-").map(Number);
  const doMes = regs.filter((r) => {
    const d = new Date(r.data_hora);
    return d.getFullYear() === ano && d.getMonth() + 1 === numero;
  });
  const actual = segundaLocal(agora);
  const grupos = new Map<string, PontoRegistro[]>();
  for (const r of doMes) {
    const chave = segundaLocal(new Date(r.data_hora));
    grupos.set(chave, [...(grupos.get(chave) ?? []), r]);
  }
  return [...grupos.entries()]
    .map(([inicio, lista]) => {
      const fim = new Date(`${inicio}T00:00:00`);
      fim.setDate(fim.getDate() + 6);
      return { inicio, fim: dataLocal(fim), horas: horas(lista, agora) || "0h 0m", semanaActual: inicio === actual };
    })
    .sort((a, b) => a.inicio.localeCompare(b.inicio));
}

/** `07:30:09` de `2026-09-15T07:30:09`. */
export function horaDoRegisto(dataHora: string): string {
  return (
    dataHora
      .replace(/\.\d+Z?$/, "")
      .replace("Z", "")
      .split("T")[1] ?? dataHora
  );
}

/** `15/09/2026` de `2026-09-15T07:30:09`. */
export function dataDoRegisto(dataHora: string): string {
  const [ano, mes, dia] = (dataHora.replace(/\.\d+Z?$/, "").split("T")[0] ?? "").split("-");
  return `${dia}/${mes}/${ano}`;
}

/** `15/09` de `2026-09-15`, como o resumo mensal do painel. */
export function diaEMes(data: string): string {
  return new Date(`${data}T00:00:00`).toLocaleDateString("pt-PT", { day: "2-digit", month: "2-digit" });
}
